import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection, doc, addDoc, deleteDoc, onSnapshot,
  query, orderBy, updateDoc,
} from 'firebase/firestore';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Pill, Plus, Trash2, Info, Search, Clock, AlertCircle,
  X, Loader2, CheckCircle, Crown, Package, Shield,
  Sparkles, Tag, ShieldAlert, Edit, FileDown, ArrowRight,
  BookOpen, Beaker
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const PharmacyPage = () => {
  const navigate = useNavigate();
  // Accès lu depuis le contexte (abonnement OU essai), au lieu du getDoc local qui
  // dupliquait la règle et ignorait trial_ends_at. Alias `isPremium` pour garder le
  // reste du fichier inchangé.
  const { user, loading: checkingPremium, hasPremiumAccess: isPremium, isTrialExpired } = useAuth();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [newMedName, setNewMedName] = useState('');
  const [newMedQuantity, setNewMedQuantity] = useState('');
  const [adding, setAdding] = useState(false);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMed, setEditingMed] = useState(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [updating, setUpdating] = useState(false);

  // Redirection si non connecté (conservée du getDoc précédent)
  useEffect(() => {
    if (!checkingPremium && !user) navigate('/login');
  }, [checkingPremium, user, navigate]);

  // Chargement des médicaments
  useEffect(() => {
    if (!isPremium || !auth.currentUser) return;
    const q = query(collection(db, 'users', auth.currentUser.uid, 'pharmacy'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMedications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return unsubscribe;
  }, [isPremium]);

  const fetchMedicationInfo = async (medicationName) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('https://api-5zm2gcbnlq-ew.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ type: 'medication_info', medicationName, userProfile: 'non_medical' }),
      });
      return await response.json();
    } catch (error) { return null; }
  };

  const handleAddMedication = async () => {
    if (!newMedName.trim()) return toast.error('Entrez le nom du médicament');
    setAdding(true);
    let docRef;
    try {
      const name = newMedName.trim();
      docRef = await addDoc(collection(db, 'users', auth.currentUser.uid, 'pharmacy'), {
        name,
        quantity: newMedQuantity.trim() || 'Non précisé',
        info: null,
        infoLoading: true,
        createdAt: new Date(),
      });
      setShowAddModal(false);
      setNewMedName('');
      setNewMedQuantity('');
      setAdding(false);
      toast.success('Médicament ajouté ! Analyse IA en cours...');

      try {
        const medInfo = await fetchMedicationInfo(name);
        await updateDoc(doc(db, 'users', auth.currentUser.uid, 'pharmacy', docRef.id), {
          info: medInfo || null,
          infoLoading: false,
        });
      } catch {
        await updateDoc(doc(db, 'users', auth.currentUser.uid, 'pharmacy', docRef.id), {
          infoLoading: false,
        });
      }
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
      setAdding(false);
    }
  };

  const handleViewDetails = (med) => {
    setSelectedMed(med);
    setShowDetailModal(true);
  };

  const handleEditQuantity = (med) => {
    setEditingMed(med);
    setEditQuantity(med.quantity);
    setShowEditModal(true);
  };

  const handleDeleteMedication = async (medId, medName) => {
    if (!window.confirm(`Supprimer ${medName} ?`)) return;
    await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'pharmacy', medId));
    toast.success('Supprimé');
  };

  const handleUpdateQuantity = async () => {
    setUpdating(true);
    await updateDoc(doc(db, 'users', auth.currentUser.uid, 'pharmacy', editingMed.id), { quantity: editQuantity.trim() });
    setShowEditModal(false);
    setUpdating(false);
    toast.success('Quantité mise à jour');
  };

  const handleExportPDF = () => {
    const pdfDoc = new jsPDF();
    pdfDoc.setFontSize(22);
    pdfDoc.setTextColor(59, 130, 246);
    pdfDoc.text('Ma Pharmacie Numérique', 14, 20);
    pdfDoc.setFontSize(10);
    pdfDoc.setTextColor(150);
    pdfDoc.text(`Date d'export : ${new Date().toLocaleDateString()}`, 14, 28);
    
    autoTable(pdfDoc, {
      startY: 35,
      head: [['Médicament', 'Quantité', 'Classe']],
      body: medications.map(m => [m.name, m.quantity, m.info?.classe || '-']),
      headStyles: { fillColor: [59, 130, 246] }
    });
    pdfDoc.save('ma-pharmacie.pdf');
  };

  const filteredMedications = medications.filter((med) =>
    med.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (checkingPremium) return (
    <DashboardLayout><div className="flex items-center justify-center min-h-screen"><Loader2 className="w-12 h-12 text-blue-500 animate-spin" /></div></DashboardLayout>
  );

  if (!isPremium) return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-2xl rotate-3">
            <Package className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">Votre Pharmacie Intelligente</h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">Organisez vos traitements et accédez aux notices simplifiées par IA en un clin d'œil.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {[
            { icon: Plus, title: "Inventaire", desc: "Suivez vos stocks de médicaments en temps réel.", color: "blue" },
            { icon: Sparkles, title: "Analyse IA", desc: "Obtenez les indications et classes thérapeutiques automatiquement.", color: "purple" },
            { icon: FileDown, title: "Export PDF", desc: "Partagez votre liste avec votre médecin en un clic.", color: "emerald" }
          ].map((feature, i) => (
            <Card key={i} className="p-8 border-none bg-white dark:bg-gray-900 shadow-xl hover:translate-y-[-5px] transition-all">
              <feature.icon className={`w-10 h-10 text-${feature.color}-500 mb-4`} />
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
            </Card>
          ))}
        </div>

        <Card className="p-12 text-center bg-gray-900 dark:bg-blue-950/20 border-none shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-10 opacity-10"><Crown className="w-40 h-40 text-yellow-500" /></div>
          <Crown className="w-16 h-16 text-yellow-500 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-4">
            {isTrialExpired ? 'Votre essai est terminé' : 'Passez à la vitesse supérieure'}
          </h2>
          <p className="text-blue-200/70 mb-8 max-w-lg mx-auto">
            {isTrialExpired
              ? "Votre essai de 7 jours est terminé. Abonnez-vous pour retrouver votre pharmacie — vos médicaments enregistrés sont conservés."
              : "L'accès complet à la pharmacie numérique est réservé à nos membres Premium."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/pricing')} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-7 text-lg rounded-2xl shadow-xl">Essayer Premium</Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-gradient-to-tr from-blue-600 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Beaker className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">Ma Pharmacie</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="flex h-2 w-2 rounded-full bg-emerald-500"></span>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                  {medications.length} Médicament{medications.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button onClick={handleExportPDF} variant="outline" className="h-14 px-6 rounded-2xl border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-50 text-gray-700 dark:text-gray-200 transition-all">
              <FileDown className="w-5 h-5 mr-2 text-gray-500" /> Exporter
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="h-14 px-8 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl shadow-lg shadow-blue-500/25 transition-all active:scale-95">
              <Plus className="w-5 h-5 mr-2" /> Ajouter
            </Button>
          </div>
        </div>

        {/* Search & Alerts */}
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2 relative group h-16">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-blue-500 transition-colors pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher dans mon inventaire..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-16 pl-14 pr-6 bg-white dark:bg-gray-900 border-none shadow-sm rounded-2xl focus:ring-2 focus:ring-blue-500 transition-all outline-none text-lg text-gray-900 dark:text-white placeholder:text-gray-400"
            />
          </div>
          <Card className="flex items-center gap-4 p-5 bg-amber-50 dark:bg-amber-950/20 border-none shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-xs text-amber-800 dark:text-amber-200 leading-snug">
              <b>Note :</b> Ces informations sont générées par IA à titre indicatif. Consultez toujours votre pharmacien.
            </p>
          </Card>
        </div>

        {/* Medications Grid */}
        {loading ? (
          <div className="grid md:grid-cols-3 gap-6 opacity-50">
            {[1,2,3].map(i => <div key={i} className="h-48 bg-gray-200 dark:bg-gray-800 animate-pulse rounded-3xl" />)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMedications.map((med) => (
              <Card
                key={med.id}
                onClick={() => handleViewDetails(med)}
                className={`group relative p-6 bg-white dark:bg-gray-900 border-none shadow-sm hover:shadow-xl hover:translate-y-[-4px] transition-all cursor-pointer rounded-[32px] overflow-hidden ${med.infoLoading ? 'animate-pulse' : ''}`}
              >
                <div className="flex justify-between items-start mb-6">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${med.infoLoading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600'}`}>
                    {med.infoLoading ? <Loader2 className="w-7 h-7 animate-spin" /> : <Pill className="w-7 h-7" />}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); handleEditQuantity(med); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500"><Edit className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteMedication(med.id, med.name); }} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 truncate uppercase">{med.name}</h3>
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                  <Package className="w-4 h-4" /> <span>Stock : {med.quantity}</span>
                </div>

                {med.info?.classe ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-[10px] font-bold uppercase tracking-wider">
                    <Tag className="w-3 h-3" /> {med.info.classe}
                  </span>
                ) : med.infoLoading ? (
                  <span className="text-xs text-blue-500 italic">Analyse en cours...</span>
                ) : null}
                
                <div className="absolute bottom-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-all">
                  <ArrowRight className="w-5 h-5 text-blue-500" />
                </div>
              </Card>
            ))}

            {/* Empty State */}
            {filteredMedications.length === 0 && (
              <div className="col-span-full py-20 text-center">
                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-10 h-10 text-gray-300" />
                </div>
                <h3 className="text-2xl font-bold text-gray-400">Aucun médicament trouvé</h3>
                <p className="text-gray-500 mt-2">Essayez un autre nom ou ajoutez-en un nouveau.</p>
              </div>
            )}
          </div>
        )}

        {/* Mini Guide - Refondu */}
        <div className="mt-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-8 w-1 bg-blue-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Guide de démarrage rapide</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Enregistrer", desc: "Saisissez le nom exact figurant sur la boîte." },
              { step: "02", title: "IA Analyse", desc: "Patientez quelques secondes le temps que l'IA identifie le produit." },
              { step: "03", title: "Consulter", desc: "Cliquez sur la carte pour voir les indications détaillées." },
              { step: "04", title: "Partager", desc: "Utilisez l'export PDF pour vos rendez-vous médicaux." }
            ].map((s, i) => (
              <div key={i} className="relative p-6 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
                <span className="absolute top-4 right-6 text-4xl font-black text-gray-100 dark:text-gray-800">{s.step}</span>
                <h4 className="font-bold text-blue-600 mb-2 relative z-10">{s.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed relative z-10">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- MODALS REFONDUS --- */}
      
      {/* Modal Ajouter */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
          <Card className="w-full max-w-md bg-white dark:bg-gray-950 p-8 rounded-[40px] shadow-2xl border-none">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-black tracking-tight">Nouvel Ajout</h2>
              <button onClick={() => setShowAddModal(false)} className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-gray-400 ml-1">Nom du produit</label>
                <input
                  autoFocus
                  value={newMedName}
                  onChange={(e) => setNewMedName(e.target.value)}
                  placeholder="Ex: PARACÉTAMOL"
                  className="w-full h-14 px-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border-none focus:ring-2 focus:ring-blue-500 outline-none transition-all text-lg font-medium text-gray-900 dark:text-white placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase text-gray-400 ml-1">Quantité en stock</label>
                <input
                  value={newMedQuantity}
                  onChange={(e) => setNewMedQuantity(e.target.value)}
                  placeholder="Ex: 2 boîtes"
                  className="w-full h-14 px-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border-none focus:ring-2 focus:ring-blue-500 outline-none transition-all text-gray-900 dark:text-white placeholder:text-gray-400"
                />
              </div>
              <Button onClick={handleAddMedication} disabled={adding} className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-lg font-bold shadow-lg shadow-blue-500/20">
                {adding ? <Loader2 className="w-6 h-6 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Détails */}
      {showDetailModal && selectedMed && (
        <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-md flex items-center justify-center z-[100] p-4 overflow-y-auto animate-in zoom-in-95 duration-200">
          <Card className="w-full max-w-2xl bg-white dark:bg-gray-950 rounded-[48px] shadow-2xl overflow-hidden border-none my-8">
            <div className="relative h-32 bg-gradient-to-r from-blue-600 to-indigo-600 p-8">
              <button onClick={() => setShowDetailModal(false)} className="absolute top-6 right-6 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center text-white transition-all"><X className="w-5 h-5" /></button>
              <div className="absolute -bottom-10 left-10 w-20 h-20 bg-white dark:bg-gray-900 rounded-3xl shadow-xl flex items-center justify-center border-4 border-white dark:border-gray-950">
                <Pill className="w-10 h-10 text-blue-600" />
              </div>
            </div>
            
            <div className="p-10 pt-16">
              <h2 className="text-4xl font-black uppercase mb-1">{selectedMed.name}</h2>
              <p className="text-gray-500 font-medium mb-8 flex items-center gap-2">
                <Package className="w-4 h-4" /> Stock actuel : {selectedMed.quantity}
              </p>

              {fetchingInfo ? (
                <div className="py-20 text-center space-y-4">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto" />
                  <p className="text-gray-400 font-medium italic">Recherche dans la base de données...</p>
                </div>
              ) : selectedMed.info ? (
                <div className="grid gap-8">
                  {selectedMed.info.classe && (
                    <div className="p-6 bg-blue-50/50 dark:bg-blue-900/10 rounded-[32px]">
                      <h4 className="flex items-center gap-2 text-xs font-black uppercase text-blue-600 mb-3 tracking-widest">
                        <Tag className="w-4 h-4" /> Classe
                      </h4>
                      <p className="text-blue-900 dark:text-blue-100 font-semibold">{selectedMed.info.classe}</p>
                    </div>
                  )}
                  {selectedMed.info.indications && (
                    <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-[32px]">
                      <h4 className="flex items-center gap-2 text-xs font-black uppercase text-gray-400 mb-3 tracking-widest">
                        <Info className="w-4 h-4" /> Indications générales
                      </h4>
                      <p className="text-gray-700 dark:text-gray-300 leading-relaxed italic">"{selectedMed.info.indications}"</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-8 bg-gray-50 dark:bg-gray-900 rounded-3xl text-center">
                  <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-400">Aucune information détaillée n'a pu être récupérée.</p>
                </div>
              )}

              <Button onClick={() => setShowDetailModal(false)} className="w-full h-16 bg-gray-900 dark:bg-white dark:text-black text-white rounded-2xl font-bold mt-10">
                Fermer l'aperçu
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Edit Quantity */}
      {showEditModal && editingMed && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <Card className="w-full max-w-sm bg-white dark:bg-gray-950 p-8 rounded-[40px] shadow-2xl">
            <h3 className="text-xl font-bold mb-6">Mettre à jour le stock</h3>
            <input
              value={editQuantity}
              onChange={(e) => setEditQuantity(e.target.value)}
              className="w-full h-14 px-5 bg-gray-50 dark:bg-gray-900 rounded-2xl border-none focus:ring-2 focus:ring-blue-500 outline-none transition-all mb-6 text-gray-900 dark:text-white"
            />
            <div className="flex gap-3">
              <Button onClick={() => setShowEditModal(false)} variant="ghost" className="flex-1 rounded-xl h-12">Annuler</Button>
              <Button onClick={handleUpdateQuantity} disabled={updating} className="flex-1 bg-blue-600 text-white rounded-xl h-12">
                {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer"}
              </Button>
            </div>
          </Card>
        </div>
      )}

    </DashboardLayout>
  );
};

export default PharmacyPage;